import { ChevronDown, PackagePlus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  buildImageProxyUrl,
  formatPrice,
  useAdminOrderSellableVariants,
  useStoreCategories,
  useTranslation,
} from '@dukanti/shared';
import type { AdminOrderSellableVariant, TranslationKey } from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export interface AdminPreparedOrderSellablePickerProps {
  readonly storeId: string | null;
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  readonly selectedVariantIds: ReadonlySet<string>;
  readonly onAdd: (variant: AdminOrderSellableVariant) => void;
}

const VISIBLE_SELLABLE_LIMIT = 30;
const SELLABLE_QUERY_LIMIT = VISIBLE_SELLABLE_LIMIT + 1;

function unavailableKey(variant: AdminOrderSellableVariant): TranslationKey {
  if (variant.unavailable_reason === 'out_of_stock') {
    return 'admin.orders.prepare.picker.outOfStock' as TranslationKey;
  }
  if (variant.unavailable_reason === 'inactive_variant') {
    return 'admin.orders.prepare.picker.inactive' as TranslationKey;
  }
  return 'admin.orders.prepare.picker.unavailable' as TranslationKey;
}

function ProductThumbnail({
  variant,
}: {
  readonly variant: AdminOrderSellableVariant;
}): React.JSX.Element {
  if (variant.thumbnail_path) {
    return (
      <img
        src={buildImageProxyUrl(env.VITE_SUPABASE_URL, 'product-images', variant.thumbnail_path)}
        alt={variant.product_name}
        data-testid="prepared-order-product-thumbnail"
        className="h-16 w-16 shrink-0 rounded-md object-cover"
      />
    );
  }

  return (
    <div
      aria-hidden="true"
      className="bg-muted flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md"
    >
      <PackagePlus className="text-muted-foreground h-8 w-8" />
    </div>
  );
}

export function AdminPreparedOrderSellablePicker(
  props: AdminPreparedOrderSellablePickerProps,
): React.JSX.Element {
  const { t, lang } = useTranslation();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const categories = useStoreCategories(supabase, props.storeId);
  useEffect(() => setCategoryId(null), [props.storeId]);
  const sellables = useAdminOrderSellableVariants(supabase, {
    storeId: props.storeId,
    query: props.query,
    categoryId,
    limit: SELLABLE_QUERY_LIMIT,
  });
  const sellableRows = sellables.data ?? [];
  const visibleSellables = sellableRows.slice(0, VISIBLE_SELLABLE_LIMIT);
  const hasMoreSellables = sellableRows.length > VISIBLE_SELLABLE_LIMIT;
  const categorySelectId = 'prepared-category-filter';

  return (
    <section className="border-border bg-card shadow-admin-card rounded-lg border p-4">
      <div className="mb-4">
        <h2 className="font-display text-foreground text-xl font-semibold">
          {t('admin.orders.prepare.picker.title' as TranslationKey)}
        </h2>
        <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_220px]">
          <label className="text-muted-foreground block text-sm" htmlFor="prepared-search">
            {t('admin.orders.prepare.picker.searchLabel' as TranslationKey)}
            <span className="relative mt-2 block">
              <Search className="text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                id="prepared-search"
                value={props.query}
                onChange={(event) => props.onQueryChange(event.target.value)}
                className="ps-10"
                placeholder={t('admin.orders.prepare.picker.searchPlaceholder' as TranslationKey)}
              />
            </span>
          </label>
          <label className="text-muted-foreground block text-sm" htmlFor={categorySelectId}>
            {t('admin.orders.prepare.picker.categoryLabel' as TranslationKey)}
            <span className="relative mt-2 block">
              <select
                id={categorySelectId}
                value={categoryId ?? ''}
                disabled={categories.isLoading || categories.categories.length === 0}
                onChange={(event) => setCategoryId(event.target.value || null)}
                className="border-input bg-card text-foreground ring-offset-background hover:border-admin-lime hover:bg-admin-surface-muted focus-visible:border-admin-lime focus-visible:ring-admin-lime/20 h-10 w-full cursor-pointer appearance-none rounded-md border px-3 pe-10 text-start text-sm font-medium shadow-sm transition-colors [-moz-appearance:none] [-webkit-appearance:none] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <option value="">
                  {categories.isLoading
                    ? t('admin.orders.prepare.picker.categoryLoading' as TranslationKey)
                    : t('admin.orders.prepare.picker.categoryAll' as TranslationKey)}
                </option>
                {categories.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <ChevronDown className="text-foreground/70 pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2" />
            </span>
          </label>
        </div>
      </div>

      {sellables.isLoading ? (
        <p className="text-muted-foreground text-sm">
          {t('admin.orders.prepare.picker.loading' as TranslationKey)}
        </p>
      ) : null}
      {sellables.isError ? (
        <p className="text-destructive text-sm">
          {t('admin.orders.prepare.picker.error' as TranslationKey)}
        </p>
      ) : null}
      {!sellables.isLoading && !sellables.isError && visibleSellables.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t('admin.orders.prepare.picker.empty' as TranslationKey)}
        </p>
      ) : null}

      <div className="space-y-3">
        {visibleSellables.map((variant) => {
          const selected = props.selectedVariantIds.has(variant.variant_id);
          return (
            <article
              key={variant.variant_id}
              className="border-border bg-background flex gap-3 rounded-lg border p-3"
            >
              <ProductThumbnail variant={variant} />
              <div className="min-w-0 flex-1 text-start">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-foreground truncate text-sm font-semibold">
                    {variant.product_name}
                  </h3>
                  {!variant.product_published ? (
                    <Badge variant="secondary">
                      {t('admin.orders.prepare.picker.draft' as TranslationKey)}
                    </Badge>
                  ) : null}
                </div>
                <p className="text-muted-foreground mt-1 text-xs">{variant.option_summary}</p>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span>{formatPrice(variant.effective_price_cents, lang)}</span>
                  <span>
                    {t('admin.orders.prepare.picker.stock' as TranslationKey, {
                      count: variant.available_stock,
                    })}
                  </span>
                  {variant.barcode ? (
                    <span>
                      {t('admin.orders.prepare.picker.barcode' as TranslationKey)} {variant.barcode}
                    </span>
                  ) : null}
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={!variant.is_selectable || selected}
                onClick={() => props.onAdd(variant)}
              >
                {selected
                  ? t('admin.orders.prepare.picker.selected' as TranslationKey)
                  : variant.is_selectable
                    ? t('admin.orders.prepare.picker.add' as TranslationKey)
                    : t(unavailableKey(variant))}
              </Button>
            </article>
          );
        })}
      </div>
      {hasMoreSellables ? (
        <p className="text-muted-foreground mt-3 text-xs">
          {t('admin.orders.prepare.picker.capped' as TranslationKey, {
            count: VISIBLE_SELLABLE_LIMIT,
          })}
        </p>
      ) : null}
    </section>
  );
}
